import { createContext, useState, ReactNode } from 'react';
import Snackbar from '@mui/material/Snackbar';
import Alert, { AlertColor } from '@mui/material/Alert';
import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Error';
import CloseIcon from '@mui/icons-material/Close';
import { ReactComponent as ErrorIcon } from '../../assets/icons/diamond-icon.svg';
import { secondaryGreen, secondaryOrange, secondaryPink } from '../color';

interface INotification {
  open: boolean;
  type: AlertColor | undefined;
  content: string;
}

interface INotificationContext {
  notify: {
    success: (successMsg: string) => void;
    error: (errorMsg: string) => void;
    warning: (warningMsg: string) => void;
  };
}

const Notification = createContext<INotificationContext>({} as INotificationContext);

export default Notification;

export const NotificationProvider = ({ children }: { children: ReactNode }) => {
  const [notification, setNotification] = useState<INotification>({
    open: false,
    type: undefined,
    content: '',
  });

  const onClose = (event: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setNotification({ ...notification, open: false });
  };

  const getNotification = () => ({
    success: (successMsg: string) =>
      setNotification({
        open: true,
        type: 'success',
        content: successMsg,
      }),
    error: (errorMsg: string) => {
      setNotification({
        open: true,
        type: 'error',
        content: errorMsg,
      });
    },
    warning: (warningMsg: string) => {
      setNotification({
        open: true,
        type: 'warning',
        content: warningMsg,
      });
    },
  });

  const notify = getNotification();

  const sxAlert = {
    display: 'flex',
    alignItems: 'center',
    background:
      notification.type === 'error'
        ? secondaryPink[700]
        : notification.type === 'warning'
        ? secondaryOrange[401]
        : secondaryGreen[700],
    '.MuiAlert-message': {
      fontSize: '1rem',
      fontFamily: 'Gellix-Semibold',
      color: '#fff',
      lineHeight: '1.5rem',
      letterSpacing: '0.6px',
    },
  };

  const sxIcon = {
    width: '24px',
    height: '24px',
    fill: '#fff',
  };

  return (
    <Notification.Provider
      value={{
        notify,
      }}
    >
      {children}
      <Snackbar
        open={notification.open}
        anchorOrigin={{ horizontal: 'center', vertical: 'top' }}
        autoHideDuration={3000}
        onClose={onClose}
        sx={{ marginTop: '70px' }}
      >
        <div>
          <Alert
            sx={sxAlert}
            severity={notification.type}
            iconMapping={{
              success: <CheckCircleIcon style={sxIcon} />,
              error: <ErrorIcon style={sxIcon} />,
              warning: <WarningIcon style={sxIcon} />,
            }}
            action={
              <CloseIcon
                style={{
                  width: 20,
                  height: 20,
                  cursor: 'pointer',
                  marginTop: '-22px',
                  marginLeft: '10px',
                  fill: '#fff',
                }}
                onClick={onClose}
              />
            }
          >
            {notification.content}
          </Alert>
        </div>
      </Snackbar>
    </Notification.Provider>
  );
};
